import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "Commercial Photography",
  "Commercial Videography",
  "Brand Film",
  "Content Package",
  "Corporate Portraits",
  "Other"
];

const DEFAULTS = {
  contact_hero_label: "Contact",
  contact_hero_title: "Let's Build Something That Elevates Your Brand.",
  contact_hero_desc: "Ready to create powerful visual content? Get in touch and let's discuss your project.",
  contact_email: "create@obracreatives.co.nz",
  contact_phone: "+64 21 029 22558",
  contact_location: "Manawatu, New Zealand",
  contact_faq_1_q: "What's your typical turnaround time?",
  contact_faq_1_a: "Project timelines vary based on scope. Simple shoots can be delivered within 1-2 weeks, while comprehensive campaigns may take 4-6 weeks. We'll provide a detailed timeline during our initial consultation.",
  contact_faq_2_q: "Do you work with businesses outside your region?",
  contact_faq_2_a: "Absolutely. While we're based in Manawatu, we proudly serve clients throughout New Zealand. For projects outside our immediate region, we'll coordinate travel and logistics as part of our consultation process to ensure seamless delivery.",
  contact_faq_3_q: "What's included in a typical project?",
  contact_faq_3_a: "Each project includes pre-production planning, on-location or studio shooting, professional editing, and delivery of final assets. Specific deliverables are outlined in your custom proposal.",
  contact_faq_4_q: "Do you offer ongoing content packages?",
  contact_faq_4_a: "Yes, we offer monthly retainer packages for businesses that need consistent content. This includes regular shoots and priority scheduling.",
};

export default function Contact() {
  const [content, setContent] = useState({});
  const [formData, setFormData] = useState({
    name: "", email: "", company: "", phone: "", service_interest: "", message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const c = (key) => content[key] || DEFAULTS[key] || "";

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => setContent(r.data || {})).catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Inquiry submitted successfully! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: "",
        service_interest: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-40">
      <SEO
        title="Contact"
        description="Contact OBRA Creatives to book a photographer or videographer in Manawatū, Palmerston North or Wellington. Get a quote for your shoot or video project."
        path="/contact"
      />
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">{c("contact_hero_label")}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#EDEDED] mt-4 mb-6 leading-tight">{c("contact_hero_title")}</h1>
            <p className="text-[#A3A3A3] text-lg leading-relaxed">{c("contact_hero_desc")}</p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 md:py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <h2 className="text-2xl font-bold text-[#EDEDED] mb-8">
                Get in Touch
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#121212] border border-[#262626] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#D4C5B0]" />
                  </div>
                  <div>
                    <h3 className="text-[#EDEDED] font-semibold mb-1">Email</h3>
                    <a href={`mailto:${c("contact_email")}`} className="text-[#A3A3A3] hover:text-[#D4C5B0] transition-colors">{c("contact_email")}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#121212] border border-[#262626] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#D4C5B0]" />
                  </div>
                  <div>
                    <h3 className="text-[#EDEDED] font-semibold mb-1">Phone</h3>
                    <a href={`tel:${c("contact_phone").replace(/\s/g, "")}`} className="text-[#A3A3A3] hover:text-[#D4C5B0] transition-colors">{c("contact_phone")}</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#121212] border border-[#262626] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#D4C5B0]" />
                  </div>
                  <div>
                    <h3 className="text-[#EDEDED] font-semibold mb-1">Location</h3>
                    <p className="text-[#A3A3A3]">{c("contact_location")}</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-[#262626]">
                <h3 className="text-[#EDEDED] font-semibold mb-4">
                  Response Time
                </h3>
                <p className="text-[#A3A3A3] text-sm">
                  We typically respond within 24-48 business hours. For urgent inquiries, please call us directly.
                </p>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2"
            >
              {isSubmitted ? (
                <div className="bg-[#121212] border border-[#262626] p-12 text-center">
                  <div className="w-16 h-16 bg-[#D4C5B0] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-[#050505]" />
                  </div>
                  <h2 className="text-2xl font-bold text-[#EDEDED] mb-4">
                    Thank You!
                  </h2>
                  <p className="text-[#A3A3A3] mb-8">
                    Your inquiry has been submitted successfully. We'll be in touch within 24-48 hours.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="btn-secondary text-sm uppercase tracking-wider"
                  >
                    Send Another Inquiry
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="bg-[#121212] border border-[#262626] p-8 md:p-12"
                >
                  <h2 className="text-2xl font-bold text-[#EDEDED] mb-8">
                    Start a Conversation
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-[#EDEDED] text-sm font-medium mb-2"
                      >
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="input-name"
                        className="form-input w-full px-4 py-3"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="block text-[#EDEDED] text-sm font-medium mb-2"
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="input-email"
                        className="form-input w-full px-4 py-3"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label
                        htmlFor="company"
                        className="block text-[#EDEDED] text-sm font-medium mb-2"
                      >
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="input-company"
                        className="form-input w-full px-4 py-3"
                        placeholder="Your company name"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-[#EDEDED] text-sm font-medium mb-2"
                      >
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="input-phone"
                        className="form-input w-full px-4 py-3"
                        placeholder="+64 21 234 567"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label
                      htmlFor="service_interest"
                      className="block text-[#EDEDED] text-sm font-medium mb-2"
                    >
                      Service Interest
                    </label>
                    <select
                      id="service_interest"
                      name="service_interest"
                      value={formData.service_interest}
                      onChange={handleChange}
                      data-testid="select-service"
                      className="form-input w-full px-4 py-3 appearance-none cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      {serviceOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-8">
                    <label
                      htmlFor="message"
                      className="block text-[#EDEDED] text-sm font-medium mb-2"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      data-testid="input-message"
                      className="form-input w-full px-4 py-3 resize-none"
                      placeholder="Tell us about your project..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="submit-button"
                    className="btn-primary w-full md:w-auto inline-flex items-center justify-center gap-2 text-sm uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin w-4 h-4 border-2 border-[#050505] border-t-transparent rounded-full" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Inquiry
                        <Send size={16} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section data-testid="faq-section" className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
            <span className="text-[#D4C5B0] text-sm uppercase tracking-[0.3em] font-medium">FAQ</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#EDEDED] mt-4">Frequently Asked Questions</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[1, 2, 3, 4].map((i, index) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#121212] border border-[#262626] p-8">
                <h3 className="text-lg font-bold text-[#EDEDED] mb-3">{c(`contact_faq_${i}_q`)}</h3>
                <p className="text-[#A3A3A3] leading-relaxed">{c(`contact_faq_${i}_a`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
